import { useState, useCallback } from 'react';
import axios from 'axios';
import { ApiError } from '../services/api/errors';
import { useAuth } from '../components/auth/AuthContext';
import { supabase } from '../lib/supabaseClient';
import { SearchMetadata } from '../types/search';
import { ChatHistoryService } from '../services/chatHistory';
import { getVisitorId } from '../services/fingerprint';

interface AnalysisState {
  isAnalyzing: boolean;
  error: string | null;
}

interface FootyResponse {
  result: string;
  classification: string;
  classificationNo: number;
  translatedQuery: string;
  method: string;
}

// Function to get or create session ID
const getOrCreateSessionId = (): string => {
  let sessionId = sessionStorage.getItem('footyAI_sessionId');
  if (!sessionId) {
    sessionId = crypto.randomUUID();
    sessionStorage.setItem('footyAI_sessionId', sessionId);
  }
  return sessionId;
};

// Create axios instance with appropriate timeouts and retries
const api = axios.create({
  timeout: 640000, // 640 seconds (10.6 minutes) for all queries
  headers: {
    'Accept': 'application/json',
    'Content-Type': 'application/json'
  }
});

// Add retry logic with exponential backoff
api.interceptors.response.use(undefined, async (err) => {
  const { config } = err;
  if (!config || !config.retry) {
    return Promise.reject(err);
  }

  config.retryCount = config.retryCount || 0;

  if (config.retryCount >= config.retry) {
    return Promise.reject(err);
  }

  // Exponential backoff with jitter
  const backoffDelay = Math.min(
    1000 * Math.pow(2, config.retryCount) + Math.random() * 1000,
    10000
  );

  config.retryCount += 1;

  // Wait for backoff delay
  await new Promise(resolve => setTimeout(resolve, backoffDelay));

  return api(config);
});

const validateResponse = (data: any): FootyResponse => {
  return {
    result: data?.result || 'No results available',
    classification: data?.classification || '',
    classificationNo: data?.classificationNo || 0,
    translatedQuery: data?.translatedQuery || '',
    method: data?.method || ''
  };
};

export const useFootyAI = () => {
  const [state, setState] = useState<AnalysisState>({
    isAnalyzing: false,
    error: null,
  });

  const { user } = useAuth();

  const analyze = useCallback(async (
    query: string, 
    deepAnalysis: boolean = false,
    metadata?: SearchMetadata
  ) => {
    if (state.isAnalyzing) {
      return null;
    }

    setState({ isAnalyzing: true, error: null });

    try {
      // Get session ID and visitor ID (only for anonymous users)
      const sessionId = getOrCreateSessionId();
      const visitorId = !user ? await getVisitorId() : null;

      const history = ChatHistoryService.getHistory();
      const formattedHistory = history.length > 0 
        ? ChatHistoryService.formatHistoryForQuery(query)
        : '';

      const formattedQuery = `*New User Message*\n${query.trim()}\n${
        formattedHistory ? `\n*Chat History*\n${formattedHistory}` : ''
      }`;

      const requestBody = {
        query: formattedQuery,
        date: new Date().toISOString(),
        result: '',
        classification: '',
        classificationNo: 0,
        translatedQuery: '',
        method: 'web',
        channel: deepAnalysis ? parseInt(import.meta.env.VITE_DEEP_ANALYSIS_CHANNEL || '1', 10) : 0,
        chatHistory: formattedHistory,
        searchId: metadata?.searchId,
        searchCategory: metadata?.searchCategory,
        userId: user?.id // Send user ID if authenticated
      };

      // Set up headers
      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      };

      // Only add session/visitor headers for anonymous users
      if (!user) {
        headers['X-Session-ID'] = sessionId;
        headers['X-Visitor-ID'] = visitorId!;
      }

      // For authenticated users, get current session token
      if (user) {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) {
          throw new Error('Authentication token not found');
        }
        headers['Authorization'] = `Bearer ${session.access_token}`;
      }

      const response = await fetch('/api/analyze', {
        method: 'POST',
        headers,
        body: JSON.stringify(requestBody),
        credentials: 'include'
      });

      if (!response.ok) {
        const error = await response.json();
        throw new ApiError(
          error.error || 'Failed to analyze query',
          response.status,
          error.code || 'ANALYSIS_ERROR'
        );
      }

      const data = await response.json();

      if (data.result && data.result !== 'No results available') {
        ChatHistoryService.addMessage('user', query);
        ChatHistoryService.addMessage('assistant', data.result);
      }

      setState(prev => ({ ...prev, isAnalyzing: false }));
      return data;
    } catch (error) {
      console.error('Analysis failed:', error);
      
      let errorMessage = 'Failed to analyze query. Please try again.';
      
      if (error instanceof ApiError && error.code === 'SUBSCRIPTION_EXPIRED') {
        errorMessage = `${error.message} <a href="/billing" class="text-blue-500 hover:text-blue-700 underline">Go to Billing</a>`;
      } else if (error instanceof ApiError && error.code === 'NO_CREDITS') {
        errorMessage = `${error.message} <a href="/billing" class="text-blue-500 hover:text-blue-700 underline">Go to Billing</a>`;
      } else if (error instanceof ApiError) {
        errorMessage = error.message || errorMessage;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
        
      setState(prev => ({ ...prev, isAnalyzing: false, error: errorMessage }));
      return null;
    }
  }, [state.isAnalyzing, user]);

  return {
    analyze,
    isAnalyzing: state.isAnalyzing,
    error: state.error,
  };
};